// DeliveryScreen.tsx
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  MdLocalShipping,
  MdInfoOutline,
  MdAccessTime,
  MdPhone,
  MdCardGiftcard,
} from "react-icons/md";
import { useTheme } from "../../common/hooks/useTheme";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { MobileHeader } from "../../common/components/mobileHeader/MobileHeader";
import { ProductHeader } from "../../common/components/productHeader/ProductHeader";
import { DeliveryRule } from "../../common/types/types";
import styles from "./DeliveryScreen.module.scss";
import { EmptyState } from "../../common/components/emptyState/EmptyState";
import { PageMeta } from "../../common/components/pageMeta/PageMeta";

interface GroupedRules {
  radius: number;
  rules: Array<{
    level: number;
    value: number;
  }>;
}

export default function DeliveryScreen() {
  const { theme } = useTheme();
  const currentCluster = useSelector(selectCurrentCluster);

  const deliveryRulesByRadius = useMemo((): GroupedRules[] => {
    if (!currentCluster?.delivery?.length) return [];
    const rulesMap = new Map<number, Array<{ level: number; value: number }>>();
    currentCluster.delivery.forEach((rule: DeliveryRule) => {
      const radius = rule.radius ? Number(rule.radius) : 0;
      const existing = rulesMap.get(radius) || [];
      rulesMap.set(radius, [
        ...existing,
        {
          level: Number(rule.level),
          value: Number(rule.value),
        },
      ]);
    });
    return Array.from(rulesMap.entries())
      .map(([radius, rules]) => ({
        radius,
        rules: rules.sort((a, b) => a.level - b.level),
      }))
      .sort((a, b) => a.radius - b.radius);
  }, [currentCluster]);

  const bestOffer = useMemo((): DeliveryRule | null => {
    if (!currentCluster?.delivery?.length) return null;
    return currentCluster.delivery.reduce(
      (best: DeliveryRule, current: DeliveryRule) =>
        Number(current.value) < Number(best.value) ? current : best,
    );
  }, [currentCluster]);

  const workingHours = useMemo(() => {
    const today = new Date().getDay();
    const todaySchedule = currentCluster?.schedule?.find(
      (s) => s.sequence === today,
    );
    if (todaySchedule?.dayOff) return "Выходной";
    return todaySchedule
      ? `${todaySchedule.from} – ${todaySchedule.to}`
      : "10:00 – 22:00";
  }, [currentCluster]);

  const title = currentCluster?.delivery?.length ? "Условия доставки" : "Доставка";
  const subtitle = bestOffer
    ? Number(bestOffer.value) === 0
      ? "Бесплатная доставка"
      : `${Number(bestOffer.value)} ₽`
    : undefined;

  if (!currentCluster?.delivery?.length) {
    return (
      <div className={styles.container} style={{ backgroundColor: theme.background }}>
        <PageMeta />
        <div className={styles.mobileOnly}>
          <MobileHeader title="Доставка" />
        </div>
        <div className={styles.desktopOnly}>
          <ProductHeader
            title="Доставка"
            isFavorite={false}
            showFavorite={false}
            textColor={theme.text}
            borderColor={theme.border}
          />
        </div>
        <div className={styles.emptyWrapper}>
          <EmptyState
            icon={MdLocalShipping}
            title="Нет информации о доставке"
            description="Информация о доставке временно недоступна"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} style={{ backgroundColor: theme.background }}>
      <PageMeta />
      <div className={styles.mobileOnly}>
        <MobileHeader title={title} subtitle={subtitle} />
      </div>
      <div className={styles.desktopOnly}>
        <ProductHeader
          title={title}
          isFavorite={false}
          showFavorite={false}
          textColor={theme.text}
          borderColor={theme.border}
        />
      </div>

      <div className={styles.content}>
        <div className={styles.scrollView}>
          {bestOffer && (
            <div className={styles.bestOfferCard} style={{ backgroundColor: theme.primary }}>
              <p className={styles.bestOfferLabel}>Лучшее предложение</p>
              {Number(bestOffer.value) === 0 ? (
                <>
                  <h2 className={styles.bestOfferTitle}>Бесплатная доставка</h2>
                  <p className={styles.bestOfferSubtitle}>
                    при заказе от {Number(bestOffer.level).toLocaleString("ru-RU")} ₽
                  </p>
                </>
              ) : (
                <>
                  <h2 className={styles.bestOfferTitle}>{Number(bestOffer.value)} ₽</h2>
                  <p className={styles.bestOfferSubtitle}>
                    при заказе от {Number(bestOffer.level).toLocaleString("ru-RU")} ₽
                  </p>
                </>
              )}
            </div>
          )}

          {deliveryRulesByRadius.map(({ radius, rules }) => (
            <div key={radius} className={styles.radiusSection}>
             <h3 className={styles.radiusTitle} style={{ color: theme.text }}>До {radius / 1000} км</h3>
              {rules.map((rule, index) => {
                const nextRule = rules[index + 1];
                const fromText =
                  index === 0
                    ? `До ${nextRule?.level.toLocaleString("ru-RU") || "∞"} ₽`
                    : `От ${rule.level.toLocaleString("ru-RU")} ₽`;
                return (
                  <div
                    key={index}
                    className={styles.ruleCard}
                    style={{ backgroundColor: theme.card, borderColor: theme.border }}
                  >
                    <div className={styles.ruleIcon} style={{ backgroundColor: `${theme.primary}20` }}>
                      {rule.value === 0 ? (
                        <MdCardGiftcard size={24} color={theme.primary} />
                      ) : (
                        <MdLocalShipping size={24} color={theme.primary} />
                      )}
                    </div>
                    <div className={styles.ruleInfo}>
                      <p className={styles.ruleFrom} style={{ color: theme.textSecondary }}>
                        {fromText}
                      </p>
                      {rule.value === 0 ? (
                        <p className={styles.ruleFree} style={{ color: theme.primary }}>Бесплатно</p>
                      ) : (
                        <p className={styles.rulePrice} style={{ color: theme.primary }}>{rule.value} ₽</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          <div className={styles.infoCard} style={{ backgroundColor: theme.card, borderColor: theme.border }}>
            <div className={styles.infoRow}>
              <MdInfoOutline size={20} color={theme.primary} className={styles.infoIcon} />
              <p className={styles.infoText} style={{ color: theme.text }}>
                Стоимость доставки зависит от расстояния и суммы заказа
              </p>
            </div>
            <div className={styles.infoRow}>
              <MdAccessTime size={20} color={theme.primary} className={styles.infoIcon} />
              <p className={styles.infoText} style={{ color: theme.text }}>
                Время доставки: {workingHours}
              </p>
            </div>
            <div className={styles.infoRow}>
              <MdPhone size={20} color={theme.primary} className={styles.infoIcon} />
              <p className={styles.infoText} style={{ color: theme.text }}>
                По вопросам доставки: {currentCluster?.phone_number || "8 (800) 000-00-00"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}